import { Injectable, inject } from '@angular/core';
import { Firestore, collection, addDoc, getDocs, query, orderBy, where,
         Timestamp, doc, updateDoc, deleteDoc, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { CommunityNews } from '../interfaces/community-news.interface';

@Injectable({
  providedIn: 'root'
})
export class CommunityNewsService {
  private firestore = inject(Firestore);
  private newsCollection = collection(this.firestore, 'communityNews');

  async addNews(news: Omit<CommunityNews, 'id'>): Promise<string> {
    try {
      const newsData: any = {
        ...news,
        createdAt: Timestamp.fromDate(news.createdAt)
      };
      
      if (news.updatedAt) {
        newsData.updatedAt = Timestamp.fromDate(news.updatedAt);
      }
      
      const docRef = await addDoc(this.newsCollection, newsData);
      return docRef.id;
    } catch (error: any) {
      throw error;
    }
  }

  /** Everything, published and draft — for the admin dashboard only. */
  async getAllNews(): Promise<CommunityNews[]> {
    try {
      const q = query(this.newsCollection, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => this.mapDoc(doc));
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Published only — for every public-facing view (Home's news section,
   * the /community-news page). getAllNews() used to be reused here too,
   * with a client-side .filter(n => n.published) — meaning draft/unpublished
   * articles were sent to every visitor's browser and just hidden in JS,
   * both a wasted read and a real content leak (visible in devtools/network).
   *
   * NOTE: this combines where() with orderBy() on a different field, which
   * Firestore requires a composite index for. If this throws
   * "FAILED_PRECONDITION: The query requires an index", check the browser
   * console — the error includes a direct link that creates the exact index
   * needed in one click.
   */
  async getPublishedNews(): Promise<CommunityNews[]> {
    try {
      const q = query(
        this.newsCollection,
        where('published', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => this.mapDoc(doc));
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Real-time listener: every news post, published and draft — for the
   * admin dashboard. Mirrors the listenToAllEvents()/listenToAllPlaylists()
   * pattern already used by EventService/PlaylistService; this was missing
   * here, which is presumably why admin-dashboard.component.ts previously
   * rolled its own inline onSnapshot listener by reaching into this
   * service's private `firestore` field via bracket-notation
   * (this.newsService['firestore']) to build the query itself — bypassing
   * TypeScript's access control and duplicating the doc-mapping logic.
   */
  listenToAllNews(callback: (news: CommunityNews[]) => void): Unsubscribe {
    const allQuery = query(this.newsCollection, orderBy('createdAt', 'desc'));
    return onSnapshot(allQuery,
      (snapshot) => {
        callback(snapshot.docs.map(doc => this.mapDoc(doc)));
      }
    );
  }

  private mapDoc(doc: any): CommunityNews {
    const data = doc.data();
    return {
      id: doc.id,
      title: data['title'] || '',
      content: data['content'] || '',
      createdAt: data['createdAt']?.toDate() || new Date(),
      updatedAt: data['updatedAt']?.toDate(),
      authorId: data['authorId'] || '',
      authorName: data['authorName'],
      published: data['published'] ?? false,
      featured: data['featured'] ?? false
    } as CommunityNews;
  }

  async updateNews(id: string, updates: Partial<CommunityNews>): Promise<void> {
    try {
      const newsDoc = doc(this.firestore, 'communityNews', id);
      const updateData: any = { ...updates };
      
      if (updates.createdAt) {
        updateData.createdAt = Timestamp.fromDate(updates.createdAt);
      }
      if (updates.updatedAt) {
        updateData.updatedAt = Timestamp.fromDate(updates.updatedAt);
      }
      
      await updateDoc(newsDoc, updateData);
    } catch (error: any) {
      throw error;
    }
  }

  async deleteNews(id: string): Promise<void> {
    try {
      const newsDoc = doc(this.firestore, 'communityNews', id);
      await deleteDoc(newsDoc);
    } catch (error: any) {
      throw error;
    }
  }
}
